import React, { useCallback, useEffect, useState } from 'react';
import { usePerspective } from '../contexts/PerspectiveContext';
import { logger } from '../utils/logger';
import { adminAPI, parentAPI, parentPortalAPI } from '../services/api';
import PlaceOrderLipoderma from '../pages/client/PlaceOrderLipoderma';
import PlaceOrderNew from '../pages/client/PlaceOrderNew';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  Apartment as ApartmentIcon,
  LocationOn as LocationIcon,
  ShoppingCart as ShopIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';

/**
 * PlaceOrderRouter
 * Routes to the correct order page based on the effective user's
 * organization (BARE -> Lipoderma, EPL -> equicenta CTM).
 *
 * Parent admin special case: orders need a single ship-to / bill-to
 * which is per-clinic, so before rendering the order page we ask the
 * parent admin to pick which child clinic this order is for. The pick
 * uses the existing parentAPI.switchToChild perspective infrastructure;
 * after a successful switch we drop into the standard order flow.
 */

const ParentClinicPicker = ({ currentUser }) => {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pickingId, setPickingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await parentPortalAPI.clinics();
      if (resp?.success) {
        setClinics(Array.isArray(resp.data) ? resp.data : []);
      } else {
        setError(resp?.error || 'Failed to load your clinics');
      }
    } catch (err) {
      logger.error('Failed to load clinics for place-order picker:', err);
      setError(err?.message || 'Failed to load your clinics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handlePick = async (clinic) => {
    setPickingId(clinic.id);
    setError('');
    try {
      const resp = await parentAPI.switchToChild(
        clinic.id,
        `Place-order picker — ${clinic.clinicName || clinic.email}`,
      );
      if (!resp?.success) {
        setError(resp?.error || 'Failed to enter clinic');
        setPickingId(null);
        return;
      }
      const ctx = resp.data?.perspectiveContext;
      if (ctx) {
        sessionStorage.setItem('barebiologics_perspective', JSON.stringify(ctx));
      }
      try {
        const stored = JSON.parse(localStorage.getItem('currentUser') || 'null');
        if (stored) {
          stored.actingAs = {
            childUserId: clinic.id,
            clinicName: clinic.clinicName,
            firstName: clinic.firstName,
            lastName: clinic.lastName,
          };
          localStorage.setItem('currentUser', JSON.stringify(stored));
        }
      } catch {
        /* localStorage corrupt; non-fatal */
      }
      // Hard reload to pick up the new perspective context cleanly.
      // Soft reload via re-render works most of the time but the order
      // pages cache fetches on mount; full reload guarantees they
      // re-fetch with the switched user as the effective scope.
      window.location.reload();
    } catch (err) {
      logger.error('Failed to switch into clinic from place-order picker:', err);
      setError(err?.message || 'Failed to enter clinic');
      setPickingId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box sx={{ textAlign: 'center' }}>
          <Avatar
            sx={{
              bgcolor: '#0d9488',
              width: 64,
              height: 64,
              mx: 'auto',
              mb: 2,
            }}
          >
            <ShopIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Which clinic is this order for?
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Pick the clinic to ship and bill this order to
            {currentUser?.parentCompanyName ? ` for ${currentUser.parentCompanyName}` : ''}. You
            can place separate orders for each clinic.
          </Typography>
        </Box>

        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

        {clinics.length === 0 ? (
          <Alert severity="info">
            No active clinics linked to your account. Reach out to your account
            manager to attach a clinic before placing orders.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {clinics.map((c) => {
              const fullName = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
              const subtitle = [c.city, c.state].filter(Boolean).join(', ');
              const isPicking = pickingId === c.id;
              return (
                <Grid item xs={12} sm={6} key={c.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderColor: '#ececef',
                      transition: 'border-color 0.15s, box-shadow 0.15s',
                      '&:hover': {
                        borderColor: '#0d9488',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      },
                      opacity: pickingId && !isPicking ? 0.5 : 1,
                    }}
                  >
                    <CardActionArea
                      disabled={!!pickingId}
                      onClick={() => handlePick(c)}
                      sx={{ p: 2 }}
                    >
                      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar sx={{ bgcolor: '#E1F3FE', color: '#1F6C9F', width: 48, height: 48 }}>
                            <StorefrontIcon />
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                              {c.clinicName || fullName || c.email}
                            </Typography>
                            {fullName && c.clinicName && (
                              <Typography variant="body2" color="text.secondary" noWrap>
                                Contact: {fullName}
                              </Typography>
                            )}
                            {subtitle && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <LocationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {subtitle}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          {isPicking && <CircularProgress size={20} />}
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Stack>
    </Container>
  );
};

const PlaceOrderRouter = ({ currentUser, onOrderSubmit }) => {
  const { isInSwitchedView, perspectiveContext } = usePerspective();
  const [effectiveOrganization, setEffectiveOrganization] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Parent admin who hasn't picked a clinic yet must do so before we
  // can route to a per-clinic order page (each order targets one
  // ship-to / bill-to clinic). Once they pick, the perspective system
  // sets isInSwitchedView=true and we render the regular order page.
  const userTypeUpper = (currentUser?.userType || '').toUpperCase();
  const needsClinicPick =
    userTypeUpper === 'PARENT_ADMIN' &&
    !isInSwitchedView;

  useEffect(() => {
    if (needsClinicPick) {
      // Skip org resolution until they pick.
      setIsLoading(false);
      return;
    }
    const determineOrganization = async () => {
      setIsLoading(true);

      try {
        // If in switched view, fetch the target user's organization
        if (isInSwitchedView && perspectiveContext?.targetUserId) {
          logger.log('PlaceOrderRouter: Fetching target user organization for:', perspectiveContext.targetUserId);
          const response = await adminAPI.getUserById(perspectiveContext.targetUserId);

          if (response.success && response.data) {
            const targetUser = response.data;
            const org = targetUser.organization || targetUser.Organization;
            logger.log('PlaceOrderRouter: Target user organization:', org);
            setEffectiveOrganization(org);
          } else {
            logger.warn('PlaceOrderRouter: Failed to fetch target user, using currentUser organization');
            setEffectiveOrganization(currentUser?.organization);
          }
        } else {
          logger.log('PlaceOrderRouter: Using currentUser organization:', currentUser?.organization);
          setEffectiveOrganization(currentUser?.organization);
        }
      } catch (error) {
        logger.error('PlaceOrderRouter: Error determining organization:', error);
        setEffectiveOrganization(currentUser?.organization);
      } finally {
        setIsLoading(false);
      }
    };

    determineOrganization();
  }, [isInSwitchedView, perspectiveContext?.targetUserId, currentUser?.organization, needsClinicPick]);

  if (needsClinicPick) {
    return <ParentClinicPicker currentUser={currentUser} />;
  }

  if (isLoading) {
    return <div style={{ padding: 16 }}>Loading...</div>;
  }

  if (effectiveOrganization === 'BARE') {
    return <PlaceOrderLipoderma currentUser={currentUser} onOrderSubmit={onOrderSubmit} />;
  }

  return <PlaceOrderNew currentUser={currentUser} onOrderSubmit={onOrderSubmit} />;
};

export default PlaceOrderRouter;
